import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { teacherApi, getToken, API } from "@/lib/api";
import { ChemText } from "@/components/ChemText";
import TeacherHeader from "./TeacherHeader";
import { QRCodeSVG } from "qrcode.react";
import {
  Play, Square, Download, RefreshCw, Copy, Users,
  CheckCircle2, Clock, TrendingUp, FileText, QrCode, Maximize2, X, Share2, Link as LinkIcon,
  BarChart3, AlertTriangle,
} from "lucide-react";

export default function SessionLive() {
  const { sessionId } = useParams();
  const [data, setData] = useState(null);
  const [questionStats, setQuestionStats] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [projector, setProjector] = useState(false);
  const [endConfirm, setEndConfirm] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [dedupBusy, setDedupBusy] = useState(false);
  const [dedupConfirm, setDedupConfirm] = useState(false);
  const [sessionQuestions, setSessionQuestions] = useState(null);
  const [reportOpenFor, setReportOpenFor] = useState(null); // { question, kind }
  const [reportText, setReportText] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const timer = useRef(null);
  const tickRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await teacherApi.get(`/sessions/${sessionId}/dashboard`);
      setData(data);
    } catch (e) {
      toast.error("Błąd ładowania");
    }
  }, [sessionId]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await teacherApi.get(`/sessions/${sessionId}/question-stats`);
      setQuestionStats(data.question_stats || []);
    } catch (e) {
      toast.error("Nie udało się pobrać statystyk");
    }
  }, [sessionId]);

  const manualRefresh = async () => {
    setRefreshing(true);
    await load();
    toast.success("Odświeżono dane");
    // Keep spinner visible briefly so user gets visual confirmation
    setTimeout(() => setRefreshing(false), 500);
  };

  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);

  useEffect(() => {
    loadRef.current?.();
    timer.current = setInterval(() => loadRef.current?.(), 3000);
    return () => clearInterval(timer.current);
  }, [sessionId]);

  // Countdown ticker — computed from session.started_at + time_limit_minutes.
  // Runs only during active state; auto-stops when session ends or expires.
  useEffect(() => {
    const s = data?.session;
    if (!s || s.status !== "active" || !s.started_at || !s.time_limit_minutes) {
      setTimeLeft(null);
      return;
    }
    const start = new Date(s.started_at).getTime();
    const end = start + s.time_limit_minutes * 60 * 1000;
    const tick = () => {
      const left = Math.max(0, Math.floor((end - Date.now()) / 1000));
      setTimeLeft(left);
    };
    tick();
    tickRef.current = setInterval(tick, 1000);
    return () => clearInterval(tickRef.current);
  }, [data?.session?.status, data?.session?.started_at, data?.session?.time_limit_minutes]);

  // Auto-load the used questions once a session ends — powers the "Pytania"
  // review panel + report-correction flow (point 6).
  useEffect(() => {
    if (data?.session?.status === "ended" && !sessionQuestions) {
      teacherApi.get(`/sessions/${sessionId}/questions`)
        .then(({ data: d }) => setSessionQuestions(d.questions || []))
        .catch(() => { /* silent — panel just won't render */ });
    }
  }, [data?.session?.status, sessionId, sessionQuestions]);

  const runDedup = async () => {
    setDedupBusy(true);
    try {
      const { data: r } = await teacherApi.delete(`/sessions/${sessionId}/duplicates`);
      toast.success(
        r.removed
          ? `Usunięto ${r.removed} zdublowanych ${r.removed === 1 ? "wpis" : "wpisów"}`
          : "Brak duplikatów do usunięcia"
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się usunąć duplikatów");
    } finally {
      setDedupBusy(false);
      setDedupConfirm(false);
    }
  };

  const submitReport = async () => {
    if (!reportOpenFor || !reportText.trim()) {
      toast.error("Opisz proponowaną poprawkę");
      return;
    }
    const q = reportOpenFor.question;
    setReportBusy(true);
    try {
      // The propose endpoint expects the FULL question payload — we're
      // essentially submitting an unchanged copy of the shared question plus
      // a `correction_note` explaining the issue. Admin sees both the note
      // and the untouched snapshot when reviewing.
      await teacherApi.post("/questions/propose", {
        type: q.type,
        text: q.text,
        options: q.options || undefined,
        correct: q.correct,
        topic: q.topic || "",
        scope: q.scope || "basic_1",
        explanation: q.explanation || undefined,
        correction_of: q.id,
        correction_note: reportText.trim().slice(0, 500),
      });
      toast.success("Zgłoszenie wysłane do administratora — dziękujemy!");
      setReportOpenFor(null);
      setReportText("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się wysłać zgłoszenia");
    } finally {
      setReportBusy(false);
    }
  };


  const start = async () => {
    await teacherApi.post(`/sessions/${sessionId}/start`);
    toast.success("Kartkówka rozpoczęta");
    load();
  };
  const stop = async () => {
    try {
      await teacherApi.post(`/sessions/${sessionId}/end`);
      toast.success("Kartkówka zakończona");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się zakończyć");
    }
  };

  const copyCode = () => {
    if (data?.session?.code) {
      navigator.clipboard.writeText(data.session.code);
      toast.success("Kod skopiowany");
    }
  };

  const copyJoinLink = async () => {
    if (!data?.session?.code) return;
    const url = `${window.location.origin}/join?code=${data.session.code}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link skopiowany do schowka");
    } catch {
      toast.error("Nie udało się skopiować — skopiuj ręcznie");
    }
  };

  const shareJoinLink = async () => {
    if (!data?.session?.code) return;
    const url = `${window.location.origin}/join?code=${data.session.code}`;
    const shareData = {
      title: `Kartkówka: ${data.session.title}`,
      text: `Dołącz do kartkówki kodem ${data.session.code} lub klikając w link.`,
      url,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (e) {
        if (e.name !== "AbortError") {
          // share dialog failed for non-user-cancel reason → fallback
          copyJoinLink();
        }
      }
    } else {
      copyJoinLink();
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <TeacherHeader />
        <main className="max-w-7xl mx-auto px-6 py-10">
          <p>Ładowanie...</p>
        </main>
      </div>
    );
  }

  const { session, students, stats } = data;
  const joinUrl = `${window.location.origin}/join?code=${session.code}`;
  const joinUrlB = session.group_b_code
    ? `${window.location.origin}/join?code=${session.group_b_code}`
    : null;
  const groupsMode = session.groups_enabled && !!session.group_b_code;
  const statusColors = {
    lobby: "bg-[#FEF08A]", active: "bg-[#D1FAE5]", ended: "bg-[#E0E7FF]",
  };
  const statusLabels = { lobby: "Poczekalnia", active: "Aktywna", ended: "Zakończona" };

  // Student status label depends on both submission state AND session state:
  // - submitted → "Oddane" + score
  // - lobby (session not started yet) → "Dołączył"
  // - active/ended, not submitted → "Pisze"
  const inLobby = session.status === "lobby";

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <TeacherHeader />
      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Top section */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2 brut bg-white rounded-md p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#64748B]">
                  {session.title}
                </div>
                <div className="mt-2 flex items-baseline gap-3 flex-wrap">
                  <span className="text-xs tracking-[0.2em] uppercase font-bold">
                    {session.groups_enabled ? "Kod grupy A:" : "Kod sesji:"}
                  </span>
                  <span className="font-display font-black text-5xl tracking-wider" data-testid="session-code-display">
                    {session.code}
                  </span>
                  <Button onClick={copyCode} variant="ghost" size="sm" className="rounded-md" data-testid="copy-code-btn">
                    <Copy className="w-4 h-4" strokeWidth={2.5} />
                  </Button>
                </div>
                {session.groups_enabled && session.group_b_code && (
                  <div className="mt-2 flex items-baseline gap-3 flex-wrap">
                    <span className="text-xs tracking-[0.2em] uppercase font-bold">Kod grupy B:</span>
                    <span
                      className="font-display font-black text-5xl tracking-wider"
                      data-testid="session-code-b-display"
                    >
                      {session.group_b_code}
                    </span>
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(session.group_b_code);
                        toast.success("Kod grupy B skopiowany");
                      }}
                      variant="ghost" size="sm" className="rounded-md"
                      data-testid="copy-code-b-btn"
                    >
                      <Copy className="w-4 h-4" strokeWidth={2.5} />
                    </Button>
                    <span className="brut bg-[#F5D0FE] text-[10px] font-black uppercase px-2 py-1 rounded"
                          title="Dwie różne pule pytań, wspólne statystyki">
                      👥 Grupy A/B
                    </span>
                  </div>
                )}
                {session.class_name && (
                  <div
                    className="inline-flex items-center gap-1 brut bg-[#E0E7FF] text-xs font-bold px-2 py-0.5 rounded-md mt-2"
                    data-testid="session-class-badge"
                  >
                    Klasa: {session.class_name}
                  </div>
                )}
                <div className="flex gap-4 mt-3 text-sm text-[#334155]">
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" strokeWidth={2.5} />{session.time_limit_minutes} min
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <FileText className="w-3 h-3" strokeWidth={2.5} />{session.num_questions} pytań
                  </span>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <span className={`brut ${statusColors[session.status]} text-xs tracking-[0.15em] uppercase font-bold px-3 py-1.5 rounded-md`}>
                  {statusLabels[session.status]}
                </span>
                {session.status === "active" && timeLeft !== null && (
                  <div
                    className={`brut font-display font-black text-2xl px-3 py-1 rounded-md tabular-nums ${
                      timeLeft <= 30 ? "bg-[#FEE2E2] text-[#B91C1C] animate-pulse"
                        : timeLeft <= 120 ? "bg-[#FEF08A]" : "bg-white"
                    }`}
                    title="Do automatycznego zakończenia"
                    data-testid="session-timer"
                  >
                    ⏱ {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, "0")}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2 mt-6 flex-wrap">
              {session.status === "lobby" && (
                <Button onClick={start} className="brut brut-hover bg-[#10B981] hover:bg-[#10B981] text-white font-bold rounded-md" data-testid="start-session-btn">
                  <Play className="w-4 h-4 mr-2" strokeWidth={2.5} />Rozpocznij
                </Button>
              )}
              {session.status === "active" && (
                <Button onClick={() => setEndConfirm(true)} className="brut brut-hover bg-[#EF4444] hover:bg-[#EF4444] text-white font-bold rounded-md" data-testid="end-session-btn">
                  <Square className="w-4 h-4 mr-2" strokeWidth={2.5} />Zakończ
                </Button>
              )}
              {(session.status === "active" || session.status === "lobby") && students && students.length > 1 && (
                <Button
                  onClick={() => setDedupConfirm(true)}
                  disabled={dedupBusy}
                  variant="outline"
                  className="brut bg-white border-2 border-[#0F172A] text-[#0F172A] hover:bg-[#FEE2E2] font-bold rounded-md"
                  data-testid="dedup-btn"
                  title="Usuń duplikaty rejestracji — zachowuje najstarszy wpis per numer w dzienniku"
                >
                  🧹 Usuń duplikaty
                </Button>
              )}
              <Button
                onClick={manualRefresh}
                disabled={refreshing}
                variant="outline"
                className="brut bg-white border-2 border-[#0F172A] font-bold rounded-md"
                data-testid="refresh-btn"
              >
                <RefreshCw
                  className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`}
                  strokeWidth={2.5}
                />
                Odśwież
              </Button>
              <a
                href={`${API}/sessions/${sessionId}/download?format=pdf&token=${encodeURIComponent(getToken() || "")}`}
                target="_blank"
                rel="noopener noreferrer"
                download={`klasa-${data.session.code}.pdf`}
                className="brut brut-hover bg-[#FFEDD5] hover:bg-[#FFEDD5] text-[#0F172A] font-bold rounded-md inline-flex items-center justify-center px-4 py-2 text-sm h-9 no-underline"
                data-testid="download-class-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" strokeWidth={2.5} />PDF klasy
              </a>
              <a
                href={`${API}/sessions/${sessionId}/download?format=csv&token=${encodeURIComponent(getToken() || "")}`}
                target="_blank"
                rel="noopener noreferrer"
                download={`klasa-${data.session.code}.csv`}
                className="brut brut-hover bg-[#E0E7FF] hover:bg-[#E0E7FF] text-[#0F172A] font-bold rounded-md inline-flex items-center justify-center px-4 py-2 text-sm h-9 no-underline"
                data-testid="download-class-csv-btn"
              >
                <Download className="w-4 h-4 mr-2" strokeWidth={2.5} />CSV klasy
              </a>
              <Button
                onClick={() => setProjector(true)}
                className="brut brut-hover bg-[#FEF08A] hover:bg-[#FEF08A] text-[#0F172A] font-bold rounded-md"
                data-testid="projector-mode-btn"
              >
                <Maximize2 className="w-4 h-4 mr-2" strokeWidth={2.5} />Tryb projektora
              </Button>
              <Button
                onClick={async () => {
                  const next = !showStats;
                  setShowStats(next);
                  if (next) await loadStats();
                }}
                className={`brut brut-hover ${showStats ? "bg-[#0F172A] text-white hover:bg-[#0F172A]" : "bg-[#E0E7FF] text-[#0F172A] hover:bg-[#E0E7FF]"} font-bold rounded-md`}
                data-testid="toggle-question-stats-btn"
              >
                <BarChart3 className="w-4 h-4 mr-2" strokeWidth={2.5} />
                {showStats ? "Ukryj statystyki" : "Statystyki pytań"}
              </Button>
            </div>
          </div>

          {/* QR + Stats column */}
          <div className="grid grid-cols-2 gap-3">
            {groupsMode ? (
              <div className="col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="qr-card-groups">
                {[
                  { letter: "A", code: session.code, url: joinUrl, bg: "#DDD6FE" },
                  { letter: "B", code: session.group_b_code, url: joinUrlB, bg: "#FBCFE8" },
                ].map((g) => (
                  <div
                    key={g.letter}
                    className="brut bg-white rounded-md p-3 flex items-center gap-3"
                    data-testid={`qr-card-${g.letter}`}
                    style={{ borderColor: "#0F172A" }}
                  >
                    <div
                      className="p-2 border-2 border-[#0F172A] rounded-md flex-shrink-0"
                      style={{ backgroundColor: g.bg }}
                    >
                      <QRCodeSVG value={g.url} size={72} level="M" bgColor={g.bg} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="inline-flex items-center gap-1 text-xs tracking-[0.2em] uppercase font-bold">
                        <QrCode className="w-3 h-3" strokeWidth={3} />QR grupa {g.letter}
                      </div>
                      <div className="font-mono font-black text-lg tracking-widest mt-0.5">
                        {g.code}
                      </div>
                      <div className="flex gap-1 mt-2 flex-wrap">
                        <Button
                          onClick={() => {
                            navigator.clipboard.writeText(g.url);
                            toast.success(`Link grupy ${g.letter} skopiowany`);
                          }}
                          size="sm"
                          variant="outline"
                          className="brut bg-white border-2 border-[#0F172A] font-bold rounded-md h-7 text-[11px] px-2"
                          data-testid={`copy-join-link-${g.letter}-btn`}
                        >
                          <LinkIcon className="w-3 h-3 mr-1" strokeWidth={2.5} />Kopiuj link
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="brut bg-white rounded-md p-4 col-span-2 flex items-center gap-4" data-testid="qr-card">
                <div className="bg-white p-2 border-2 border-[#0F172A] rounded-md flex-shrink-0">
                  <QRCodeSVG value={joinUrl} size={88} level="M" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="inline-flex items-center gap-1 text-xs tracking-[0.2em] uppercase font-bold">
                    <QrCode className="w-3 h-3" strokeWidth={3} />Dołącz QR
                  </div>
                  <div className="text-sm font-bold mt-1 truncate" title={joinUrl}>{joinUrl}</div>
                  <div className="text-xs text-[#64748B] mt-1">Zeskanuj lub udostępnij link uczniom</div>
                  <div className="flex gap-2 mt-3 flex-wrap">
                    <Button
                      onClick={copyJoinLink}
                      size="sm"
                      variant="outline"
                      className="brut bg-white border-2 border-[#0F172A] font-bold rounded-md h-8 text-xs px-3"
                      data-testid="copy-join-link-btn"
                    >
                      <LinkIcon className="w-3 h-3 mr-1.5" strokeWidth={2.5} />Kopiuj link
                    </Button>
                    <Button
                      onClick={shareJoinLink}
                      size="sm"
                      className="brut brut-hover bg-[#D1FAE5] hover:bg-[#D1FAE5] text-[#0F172A] font-bold rounded-md h-8 text-xs px-3"
                      data-testid="share-join-link-btn"
                    >
                      <Share2 className="w-3 h-3 mr-1.5" strokeWidth={2.5} />Udostępnij
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <div className="brut bg-[#D1FAE5] rounded-md p-4">
              <Users className="w-5 h-5" strokeWidth={2.5} />
              <div className="text-xs tracking-[0.2em] uppercase font-bold mt-2">Dołączyło</div>
              <div className="font-display font-black text-3xl">{stats.joined}</div>
            </div>
            <div className="brut bg-[#E0F2FE] rounded-md p-4">
              <CheckCircle2 className="w-5 h-5" strokeWidth={2.5} />
              <div className="text-xs tracking-[0.2em] uppercase font-bold mt-2">Oddało</div>
              <div className="font-display font-black text-3xl">{stats.submitted}</div>
            </div>
            <div className="brut bg-[#FFEDD5] rounded-md p-4 col-span-2">
              <TrendingUp className="w-5 h-5" strokeWidth={2.5} />
              <div className="text-xs tracking-[0.2em] uppercase font-bold mt-2">Średnia klasy</div>
              <div className="font-display font-black text-3xl">{stats.average_percent}%</div>
            </div>
          </div>
        </div>

        {/* Groups A/B balance + comparative stats */}
        {data.group_stats && (
          <section className="mb-8" data-testid="groups-ab-widget">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs tracking-[0.2em] uppercase font-bold">
                👥 Grupy A vs B
              </span>
              {(() => {
                const jA = data.group_stats.A.joined;
                const jB = data.group_stats.B.joined;
                const diff = Math.abs(jA - jB);
                if (jA + jB === 0) return null;
                const balanced = diff <= 1;
                return (
                  <span
                    className={`brut text-[10px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded ${
                      balanced ? "bg-[#D1FAE5]" : "bg-[#FEE2E2]"
                    }`}
                    data-testid="groups-balance-chip"
                  >
                    {balanced
                      ? "✓ podział zrównoważony"
                      : `⚠ różnica ${diff} — przekieruj uczniów`}
                  </span>
                );
              })()}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {["A", "B"].map((letter) => {
                const g = data.group_stats[letter];
                const code = letter === "A" ? session.code : session.group_b_code;
                const color = letter === "A" ? "#DDD6FE" : "#FBCFE8";
                return (
                  <div
                    key={letter}
                    className="brut border-2 border-[#0F172A] rounded-md p-4"
                    style={{ backgroundColor: color }}
                    data-testid={`group-panel-${letter}`}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="flex items-baseline gap-3">
                        <div className="font-display font-black text-4xl">{letter}</div>
                        <div className="font-mono text-lg tracking-widest">{code || "—"}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] tracking-wide uppercase font-bold">Dołączyło</div>
                        <div className="font-display font-black text-2xl tabular-nums">{g.joined}</div>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-4 text-sm">
                      <div>
                        <span className="text-[#334155] text-xs uppercase tracking-wide font-bold">Oddało</span>
                        <div className="font-display font-black text-xl tabular-nums">
                          {g.submitted}<span className="text-[#64748B] text-sm">/{g.joined}</span>
                        </div>
                      </div>
                      <div className="border-l-2 border-[#0F172A] pl-4">
                        <span className="text-[#334155] text-xs uppercase tracking-wide font-bold">Średnia</span>
                        <div className="font-display font-black text-xl tabular-nums">{g.average_percent}%</div>
                      </div>
                    </div>
                    <div className="mt-3 h-2 bg-white brut rounded overflow-hidden">
                      <div
                        className="h-full bg-[#0F172A]"
                        style={{ width: `${Math.min(100, g.average_percent)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            {(() => {
              const a = data.group_stats.A.average_percent;
              const b = data.group_stats.B.average_percent;
              if (data.group_stats.A.submitted + data.group_stats.B.submitted < 2) return null;
              const diff = Math.abs(a - b);
              if (diff < 8) {
                return (
                  <div className="mt-2 text-xs text-[#334155]" data-testid="groups-diff-hint">
                    Obie grupy mają zbliżoną trudność (różnica {diff.toFixed(1)} pp).
                  </div>
                );
              }
              const harder = a > b ? "B" : "A";
              return (
                <div className="mt-2 text-xs text-[#B91C1C] font-bold" data-testid="groups-diff-hint">
                  ⚠ Grupa {harder} okazała się trudniejsza — różnica {diff.toFixed(1)} pp.
                  Rozważ wyrównanie puli przed kolejnym uruchomieniem.
                </div>
              );
            })()}
          </section>
        )}

        {/* Per-question statistics (toggleable) */}
        {showStats && (
          <section className="mb-8" data-testid="question-stats-section">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h2 className="font-display font-black text-2xl inline-flex items-center gap-2">
                <BarChart3 className="w-5 h-5" strokeWidth={2.5} />
                Statystyki pytań
              </h2>
              <Button
                onClick={loadStats}
                variant="outline"
                size="sm"
                className="brut bg-white border-2 border-[#0F172A] font-bold rounded-md"
                data-testid="refresh-stats-btn"
              >
                <RefreshCw className="w-3 h-3 mr-1" strokeWidth={2.5} />Odśwież
              </Button>
            </div>
            {questionStats === null ? (
              <div className="brut bg-white rounded-md p-6 text-sm text-[#64748B]">Ładowanie statystyk...</div>
            ) : questionStats.length === 0 ? (
              <div className="brut bg-white rounded-md p-6 text-sm text-[#64748B]" data-testid="no-stats-msg">
                Brak oddanych arkuszy — statystyki pojawią się gdy uczniowie zaczną oddawać.
              </div>
            ) : (
              <div className="space-y-2">
                {questionStats.map((row, idx) => {
                  const hard = row.attempts > 0 && row.success_rate < 50;
                  return (
                    <div
                      key={row.question_id}
                      className={`brut rounded-md p-3 ${hard ? "bg-[#FEE2E2]" : "bg-white"}`}
                      data-testid={`q-stat-${idx}`}
                    >
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="text-xs tracking-[0.15em] uppercase font-bold text-[#64748B] flex items-center gap-2">
                            <span>#{idx + 1}</span>
                            {row.topic && <span>· {row.topic}</span>}
                            {hard && (
                              <span className="inline-flex items-center gap-1 text-[#EF4444]">
                                <AlertTriangle className="w-3 h-3" strokeWidth={2.5} />Trudne
                              </span>
                            )}
                          </div>
                          <div className="font-bold mt-1 text-sm">
                            <ChemText text={row.text} />
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="font-display font-black text-2xl">
                            {row.success_rate}%
                          </div>
                          <div className="text-xs text-[#64748B]">
                            {row.correct}/{row.attempts}
                          </div>
                        </div>
                      </div>
                      {/* Mini bar */}
                      <div className="mt-2 h-2 bg-[#E2E8F0] rounded-md overflow-hidden border-2 border-[#0F172A]">
                        <div
                          className={`h-full ${hard ? "bg-[#EF4444]" : row.success_rate >= 75 ? "bg-[#10B981]" : "bg-[#FEF08A]"}`}
                          style={{ width: `${row.attempts > 0 ? row.success_rate : 0}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* Students list */}
        <h2 className="font-display font-black text-2xl mb-4">Uczniowie ({students.length})</h2>
        {students.length === 0 ? (
          <div className="brut bg-white rounded-md p-10 text-center" data-testid="no-students-msg">
            <p className="text-[#64748B]">Oczekiwanie na uczniów... Udostępnij im kod <b>{session.code}</b>.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {students.map((s) => (
              <div key={s.id} className="brut bg-white rounded-md overflow-hidden" data-testid={`student-row-${s.name}`}>
                <div
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-[#F8FAFC] gap-4 flex-wrap"
                  onClick={() => setExpanded(expanded === s.id ? null : s.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="brut bg-[#FEF08A] w-10 h-10 flex items-center justify-center rounded-md font-bold">
                      {s.roll_number ?? s.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-bold">
                        {s.name}
                        {s.roll_number != null && (
                          <span className="text-[#64748B] font-normal"> · nr {s.roll_number}</span>
                        )}
                      </div>
                      <div className="text-xs text-[#64748B]">
                        {s.status === "submitted"
                          ? "Oddane"
                          : inLobby
                          ? "Dołączył — czeka na start"
                          : "W trakcie"}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {s.status === "submitted" ? (
                      <div className="text-right">
                        <div className="font-display font-black text-2xl">{s.percent}%</div>
                        <div className="text-xs text-[#64748B]">{s.score}/{s.total}</div>
                      </div>
                    ) : inLobby ? (
                      <span
                        className="brut bg-[#E0F2FE] text-xs tracking-[0.15em] uppercase font-bold px-2 py-1 rounded-md"
                        data-testid={`student-status-joined-${s.name}`}
                      >
                        Dołączył
                      </span>
                    ) : (
                      <span
                        className="brut bg-[#FEF08A] text-xs tracking-[0.15em] uppercase font-bold px-2 py-1 rounded-md"
                        data-testid={`student-status-writing-${s.name}`}
                      >
                        Pisze
                      </span>
                    )}
                    {s.status === "submitted" && (
                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <a
                          href={`${API}/public/sessions/${session.code}/student/${s.id}/download?format=pdf`}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={`${session.code}-${s.name}.pdf`}
                          className="brut bg-white border-2 border-[#0F172A] font-bold rounded-md inline-flex items-center justify-center px-3 h-8 text-xs no-underline text-[#0F172A]"
                          data-testid={`download-student-pdf-${s.name}`}
                        >
                          PDF
                        </a>
                        <a
                          href={`${API}/public/sessions/${session.code}/student/${s.id}/download?format=csv`}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={`${session.code}-${s.name}.csv`}
                          className="brut bg-white border-2 border-[#0F172A] font-bold rounded-md inline-flex items-center justify-center px-3 h-8 text-xs no-underline text-[#0F172A]"
                          data-testid={`download-student-csv-${s.name}`}
                        >
                          CSV
                        </a>
                      </div>
                    )}
                  </div>
                </div>
                {expanded === s.id && s.answers && s.answers.length > 0 && (
                  <div className="border-t-2 border-[#0F172A] bg-[#F8FAFC] p-4 space-y-2">
                    {s.answers.map((a, idx) => (
                      <div key={idx} className="text-sm">
                        <div className="font-bold">{idx + 1}. <ChemText text={a.question_text} /></div>
                        <div className="text-[#334155]">
                          Odp: <b><ChemText text={a.given || "(brak)"} /></b> · Poprawna: <b><ChemText text={a.correct_answer} /></b>
                          {" "}
                          <span className={a.is_correct ? "text-[#10B981] font-bold" : "text-[#EF4444] font-bold"}>
                            [{a.is_correct ? "OK" : "X"}]
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Point 6 — question review panel + report-correction flow.
            Available only after the session has ended so we don't accidentally
            surface the correct answers while students are still writing. */}
        {session.status === "ended" && sessionQuestions && sessionQuestions.length > 0 && (
          <section className="mt-10" data-testid="session-questions-panel">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4" strokeWidth={2.5} />
              <h2 className="text-xs tracking-[0.2em] uppercase font-bold">
                Użyte pytania ({sessionQuestions.length})
              </h2>
            </div>
            <div className="space-y-3">
              {sessionQuestions.map((q, i) => (
                <div key={q.id} className="brut bg-white rounded-md p-4"
                     data-testid={`session-question-${q.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-[#64748B] font-bold">
                        Pytanie {i + 1} · {q.topic || "—"} · {q.type === "mc" ? "ABCD" : "otwarte"}
                        {q.owner_type === "shared" && (
                          <span className="ml-2 brut bg-[#D1FAE5] text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wide">
                            wspólne
                          </span>
                        )}
                      </div>
                      <div className="font-bold mt-1">
                        <ChemText text={q.text} />
                      </div>
                      {q.type === "mc" && Array.isArray(q.options) && (
                        <ul className="mt-2 space-y-1 text-sm">
                          {q.options.map((opt, oi) => {
                            const letter = String.fromCharCode(65 + oi);
                            const isCorrect = q.correct === letter;
                            return (
                              <li key={letter}
                                  className={`flex items-start gap-2 ${isCorrect ? "font-bold text-[#065F46]" : ""}`}>
                                <span className="brut rounded px-1.5 text-xs font-black bg-white">
                                  {letter}
                                </span>
                                <span><ChemText text={opt} /></span>
                                {isCorrect && <span>✓</span>}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                      {q.type !== "mc" && (
                        <div className="mt-2 text-sm">
                          <b>Odpowiedź: </b><ChemText text={q.correct} />
                        </div>
                      )}
                      {q.explanation && (
                        <div className="mt-2 text-sm text-[#334155]">
                          <b>Wyjaśnienie: </b><ChemText text={q.explanation} />
                        </div>
                      )}
                    </div>
                    {q.owner_type === "shared" ? (
                      <Button
                        onClick={() => { setReportOpenFor({ question: q }); setReportText(""); }}
                        variant="outline"
                        className="brut bg-white border-2 border-[#0F172A] font-bold rounded-md"
                        data-testid={`report-question-${q.id}`}
                        title="Zgłoś administratorowi błąd lub sugestię"
                      >
                        <AlertTriangle className="w-4 h-4 mr-1" strokeWidth={2.5} />
                        Zgłoś poprawkę
                      </Button>
                    ) : (
                      <span className="text-xs text-[#64748B] italic self-center">
                        Twoje pytanie — edytuj w bazie pytań
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Report correction modal */}
      {reportOpenFor && (
        <div
          className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
          onClick={() => !reportBusy && setReportOpenFor(null)}
          data-testid="report-modal"
        >
          <div className="brut border-4 border-[#0F172A] bg-white rounded-md max-w-lg w-full p-6"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <h2 className="font-display font-black text-xl">Zgłoś poprawkę</h2>
                <p className="text-xs text-[#64748B] mt-1">
                  Administrator zobaczy oryginalne pytanie i Twoją uwagę.
                </p>
              </div>
              <button
                onClick={() => setReportOpenFor(null)}
                className="brut brut-hover border-2 border-[#0F172A] rounded p-1 bg-white"
                data-testid="report-close-btn"
                aria-label="Zamknij"
              >
                <X className="w-5 h-5" strokeWidth={2.5} />
              </button>
            </div>
            <div className="brut bg-[#F1F5F9] rounded-md p-3 mt-4 text-sm">
              <b>Treść:</b> <ChemText text={reportOpenFor.question.text} />
              <div className="mt-1"><b>Poprawna:</b> <ChemText text={reportOpenFor.question.correct} /></div>
            </div>
            <label className="block mt-4">
              <span className="text-sm font-bold">Twoja uwaga (max 500 znaków)</span>
              <textarea
                value={reportText}
                onChange={(e) => setReportText(e.target.value.slice(0, 500))}
                rows={4}
                className="mt-1 w-full brut-input border-2 border-[#0F172A] rounded-md p-2 text-sm"
                placeholder="np. Poprawna odpowiedź to B, nie A — HCl jest kwasem tlenowym…"
                data-testid="report-note-input"
              />
              <div className="text-xs text-[#64748B] text-right mt-1">
                {reportText.length}/500
              </div>
            </label>
            <div className="flex gap-2 justify-end mt-3">
              <Button
                onClick={() => setReportOpenFor(null)}
                disabled={reportBusy}
                variant="outline"
                className="brut bg-white border-2 border-[#0F172A] font-bold rounded-md"
                data-testid="report-cancel-btn"
              >
                Anuluj
              </Button>
              <Button
                onClick={submitReport}
                disabled={reportBusy || !reportText.trim()}
                className="brut brut-hover bg-[#0F172A] hover:bg-[#0F172A] text-white font-bold rounded-md"
                data-testid="report-submit-btn"
              >
                {reportBusy ? "Wysyłam..." : "Wyślij zgłoszenie"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Dedup confirmation */}
      <AlertDialog open={dedupConfirm} onOpenChange={setDedupConfirm}>
        <AlertDialogContent className="brut bg-white rounded-md border-2 border-[#0F172A]" data-testid="dedup-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-black text-2xl">Usunąć duplikaty rejestracji?</AlertDialogTitle>
            <AlertDialogDescription>
              Zachowamy najstarszy wpis dla każdego numeru w dzienniku (lub imienia, jeśli brak numeru).
              Nadmiarowe puste rejestracje — najczęściej z powodu wielokrotnego kliknięcia „Dołącz" —
              zostaną usunięte. Prace już oddane są chronione i nie zostaną skasowane.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="brut bg-white border-2 border-[#0F172A] font-bold rounded-md"
                               data-testid="dedup-cancel-btn">
              Anuluj
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={runDedup}
              className="brut brut-hover bg-[#EF4444] hover:bg-[#EF4444] text-white font-bold rounded-md"
              data-testid="dedup-confirm-btn"
            >
              Tak, usuń duplikaty
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Projector mode dialog */}
      <Dialog open={projector} onOpenChange={setProjector}>
        <DialogContent
          className="bg-white border-2 border-[#0F172A] rounded-md p-0 max-w-[100vw] w-screen h-[100vh] sm:h-auto sm:max-w-5xl"
          data-testid="projector-modal"
        >
          <button
            onClick={() => setProjector(false)}
            className="absolute top-4 right-4 brut bg-white w-12 h-12 rounded-md flex items-center justify-center z-50"
            data-testid="close-projector-btn"
          >
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>
          <div className="p-6 sm:p-10 grid sm:grid-cols-2 gap-8 items-center min-h-[60vh]">
            <div className="text-center sm:text-left">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#64748B]">Dołącz do kartkówki</div>
              <h1 className="font-display font-black text-4xl sm:text-6xl mt-2 leading-none">{session.title}</h1>
              {groupsMode ? (
                <div className="mt-6 grid grid-cols-2 gap-3">
                  {[
                    { letter: "A", code: session.code, bg: "#DDD6FE" },
                    { letter: "B", code: session.group_b_code, bg: "#FBCFE8" },
                  ].map((g) => (
                    <div
                      key={g.letter}
                      className="brut rounded-md p-4"
                      style={{ backgroundColor: g.bg }}
                      data-testid={`projector-code-${g.letter}`}
                    >
                      <div className="text-xs tracking-[0.2em] uppercase font-bold">
                        Grupa {g.letter}
                      </div>
                      <div className="font-display font-black text-5xl sm:text-6xl tracking-[0.15em] leading-none mt-1">
                        {g.code}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-6 brut bg-[#FEF08A] rounded-md p-5 inline-block">
                  <div className="text-xs tracking-[0.2em] uppercase font-bold">Kod sesji</div>
                  <div className="font-display font-black text-7xl sm:text-8xl tracking-[0.15em] leading-none mt-1">
                    {session.code}
                  </div>
                </div>
              )}
              <div className="mt-6 flex gap-4 text-sm sm:text-base text-[#334155] justify-center sm:justify-start">
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-4 h-4" strokeWidth={2.5} />{session.time_limit_minutes} min
                </span>
                <span className="inline-flex items-center gap-1">
                  <FileText className="w-4 h-4" strokeWidth={2.5} />{session.num_questions} pytań
                </span>
                <span className="inline-flex items-center gap-1">
                  <Users className="w-4 h-4" strokeWidth={2.5} />{stats.joined} uczniów
                </span>
              </div>
              {!groupsMode && (
                <div className="mt-6 flex gap-2 flex-wrap justify-center sm:justify-start">
                  <Button
                    onClick={copyJoinLink}
                    variant="outline"
                    className="brut bg-white border-2 border-[#0F172A] font-bold rounded-md"
                    data-testid="projector-copy-link-btn"
                  >
                    <LinkIcon className="w-4 h-4 mr-2" strokeWidth={2.5} />Kopiuj link
                  </Button>
                  <Button
                    onClick={shareJoinLink}
                    className="brut brut-hover bg-[#D1FAE5] hover:bg-[#D1FAE5] text-[#0F172A] font-bold rounded-md"
                    data-testid="projector-share-link-btn"
                  >
                    <Share2 className="w-4 h-4 mr-2" strokeWidth={2.5} />Udostępnij link
                  </Button>
                </div>
              )}
            </div>
            <div className="flex flex-col items-center">
              {groupsMode ? (
                <div className="grid grid-cols-2 gap-4 w-full max-w-[560px]">
                  {[
                    { letter: "A", url: joinUrl, bg: "#DDD6FE" },
                    { letter: "B", url: joinUrlB, bg: "#FBCFE8" },
                  ].map((g) => (
                    <div key={g.letter} className="flex flex-col items-center" data-testid={`projector-qr-${g.letter}`}>
                      <div className="brut-lg p-4 rounded-md" style={{ backgroundColor: g.bg }}>
                        <QRCodeSVG value={g.url} size={200} level="M" bgColor={g.bg} />
                      </div>
                      <div className="mt-3 brut bg-white px-3 py-1 rounded-md">
                        <span className="text-sm font-black uppercase tracking-[0.2em]">
                          Grupa {g.letter}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <div className="brut-lg bg-white p-6 rounded-md">
                    <QRCodeSVG value={joinUrl} size={280} level="M" />
                  </div>
                  <div className="mt-4 text-center">
                    <div className="inline-flex items-center gap-2 brut bg-[#D1FAE5] px-3 py-1 rounded-md">
                      <QrCode className="w-3 h-3" strokeWidth={3} />
                      <span className="text-xs tracking-[0.2em] uppercase font-bold">Skanuj telefonem</span>
                    </div>
                    <div className="text-xs text-[#64748B] mt-2 break-all">{joinUrl}</div>
                  </div>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* End confirmation */}
      <AlertDialog open={endConfirm} onOpenChange={setEndConfirm}>
        <AlertDialogContent className="brut bg-white rounded-md border-2 border-[#0F172A]" data-testid="end-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-black text-2xl">Zakończyć kartkówkę?</AlertDialogTitle>
            <AlertDialogDescription>
              Po zakończeniu uczniowie nie będą mogli już wysłać odpowiedzi. Tę akcję możesz wykonać tylko raz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="brut bg-white border-2 border-[#0F172A] font-bold rounded-md"
              data-testid="end-cancel-btn"
            >
              Anuluj
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={stop}
              className="brut brut-hover bg-[#EF4444] hover:bg-[#EF4444] text-white font-bold rounded-md"
              data-testid="end-confirm-btn"
            >
              Tak, zakończ
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
